<script setup lang="ts">
  import { useMediaQuery } from "@vueuse/core"

  const toast = useToast()
  const route = useRoute()

  const popupBlockedRoutes = [
    'cart',
  ]

  const shouldShowPopups = computed(() => {
    const name = route.name?.toString()
    if (!name) return true
    return !popupBlockedRoutes.includes(name)
  })

  const { data: popups } = useApi<ActivePopupsResponse[]>(`/cms/popups/active-popups/panel`, {
    server: false
  })

  const usePopupTimestamp = (key: string) => {
    const cookie = useCookie<string | null>(key, {
      maxAge: 60 * 60 * 24
    })

    const get = () => (cookie.value ? Number(cookie.value) : null)

    const setNow = () => {
      cookie.value = Date.now().toString()
    }

    return { get, setNow }
  }

  const isTablet = useMediaQuery("(min-width: 640px)")

  // toast
  const toastPopups = computed(() => popups.value?.filter((p) => p.type === "toast") ?? [])

  const toastDatas = computed(() => toastPopups.value.map((p) => ({ ...p, popup: p.popup as Toast })))

  function showToast(id: number) {
    if (!shouldShowPopups.value) return
    const item = toastDatas.value.find((t) => t.id === id)
    if (!item) return
    const { popup } = item

    toast.add({
      id: id,
      orientation: "vertical",
      title: popup.title,
      description: popup.description,
      icon: popup.icon,
      color: popup.color,
      duration: popup.duration ? popup.duration * 1000 : 0,
      close: false,
      type: "background",
      actions: [
        {
          variant: "soft",
          label: popup.action.label ?? undefined,
          icon: popup.action.icon ?? undefined,
          trailing: true,
          to: popup.action.to ?? undefined,
          target: popup.action.target,
          onClick: () => handleToastAction(id),
        },
      ],
      ui: { root: "max-w-96 mx-auto", actions: "justify-end", title: `text-${popup.color}`, description: `text-${popup.color} opacity-90` },
    })
  }

  const handleToastAction = (id: number) => {
    toast.remove(id)
    const { setNow } = usePopupTimestamp(`lastToastShownAt_${id}`)
    setNow()
  }

  // alert
  const alertPopups = computed(() => popups.value?.filter((p) => p.type === "alert") ?? [])

  const alertDatas = computed(() => alertPopups.value.map((p) => ({ ...p, popup: p.popup as Alert })))

  const showAlerts = ref<number[]>([])

  const visibleAlerts = computed(() => alertDatas.value.filter((a) => showAlerts.value.includes(a.id)))

  const handleAlertAction = (id: number) => {
    showAlerts.value = showAlerts.value.filter((a) => a !== id)
    const { setNow } = usePopupTimestamp(`lastAlertShownAt_${id}`)
    setNow()
  }

  // modal
  const modalPopups = computed(() => popups.value?.filter((p) => p.type === "modal") ?? [])

  const modalQueue = ref<number[]>([])
  const currentModalId = computed(() => modalQueue.value[0] ?? null)

  const modalData = computed(() => {
    if (!currentModalId.value) return null
    const found = modalPopups.value.find((p) => p.id === currentModalId.value)
    return found ? (found.popup as Modal) : null
  })

  const isModalOpen = ref(false)

  const isIntentionalClose = ref(false)

  const handleModalClose = () => {
    if (!currentModalId.value || !shouldShowPopups.value) return
    isIntentionalClose.value = true
    const { setNow } = usePopupTimestamp(`lastModalShownAt_${currentModalId.value}`)
    setNow()
    isModalOpen.value = false
    setTimeout(() => {
      modalQueue.value.shift()
      if (modalQueue.value.length > 0) {
        nextTick(() => {
          isModalOpen.value = true
        })
      }
    }, 300)
  }

  const handleModalDismiss = () => {
    if (isIntentionalClose.value) {
      isIntentionalClose.value = false
      return
    }
    isModalOpen.value = false
    setTimeout(() => {
      modalQueue.value.shift()
      if (modalQueue.value.length > 0) {
        nextTick(() => {
          isModalOpen.value = true
        })
      }
    }, 300)
  }

  const evaluatePopups = () => {
    modalQueue.value = []
    showAlerts.value = []

    const now = Date.now()
    const INTERVAL = 24 * 60 * 60 * 1000

    modalPopups.value.forEach(({ id }) => {
      const { get } = usePopupTimestamp(`lastModalShownAt_${id}`)
      const lastShown = get()
      if (!lastShown || now - lastShown > INTERVAL) {
        modalQueue.value.push(id)
      }
    })
    if (modalQueue.value.length > 0) {
      isModalOpen.value = true
    }

    alertPopups.value.forEach(({ id }) => {
      const { get } = usePopupTimestamp(`lastAlertShownAt_${id}`)
      const lastShown = get()
      if (!lastShown || now - lastShown > INTERVAL) {
        showAlerts.value.push(id)
      }
    })

    toastPopups.value.forEach(({ id }) => {
      const { get } = usePopupTimestamp(`lastToastShownAt_${id}`)
      const lastShown = get()
      if (!lastShown || now - lastShown > INTERVAL) {
        showToast(id)
      }
    })
  }

  onMounted(() => {
    if (!shouldShowPopups.value) return
    evaluatePopups()
  })

  watch(
    [() => popups.value, () => route.name],
    ([popupsVal]) => {
      if (!popupsVal || !shouldShowPopups.value) return
      evaluatePopups()
    },
    { immediate: true }
  )
</script>

<template>
  <ClientOnly>
    <UModal
      v-model:open="isModalOpen"
      @update:open="
        (val) => {
          if (!val) handleModalDismiss()
        }
      "
      :ui="{ content: '!p-0 divide-y-0', body: '!p-4', footer: '!p-4' }"
    >
      <template #header>
        <div class="flex flex-col w-full">
          <template v-if="modalData?.isTop">
            <NuxtImg v-if="modalData?.mediaType === 'image'" class="w-full" :src="modalData?.image" />

            <VideoPlayer
              v-if="modalData?.mediaType === 'video'"
              :title="modalData?.video?.videoTitle ?? undefined"
              :poster="modalData?.video?.videoCover ?? undefined"
              :path="modalData?.video?.videoUrl ?? ''"
              rounded="rounded-none"
            />

            <PopupAudioPlayer
              v-if="modalData?.mediaType === 'audio'"
              :src="modalData?.audio?.audioUrl ?? ''"
              :title="modalData?.audio?.audioTitle ?? ''"
              :img="modalData?.audio?.audioCover ?? ''"
              :downloadable="false"
              class="p-4 sm:pt-10"
            />
          </template>

          <div class="flex items-center gap-1.5 sm:gap-3 p-4">
            <UIcon :name="modalData?.icon ?? ''" class="size-7 text-primary-500 dark:text-primary-400" />
            <h5 class="h5">{{ modalData?.title }}</h5>
          </div>

          <UButton color="neutral" variant="ghost" square class="absolute top-4 left-4 z-10" @click="handleModalClose">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 1L1 9M1 1L9 9" stroke="#8B85AD" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </UButton>
        </div>
      </template>

      <template #body>
        <div class="flex flex-col gap-6">
          <p class="body">{{ modalData?.description }}</p>
          <div v-if="!modalData?.isTop" class="overflow-hidden">
            <NuxtImg v-if="modalData?.mediaType === 'image'" class="rounded-xs w-full" :src="modalData?.image" />

            <PopupAudioPlayer
              v-if="modalData?.mediaType === 'audio'"
              :src="modalData?.audio?.audioUrl ?? ''"
              :title="modalData?.audio?.audioTitle ?? ''"
              :img="modalData?.audio?.audioCover ?? ''"
              :downloadable="false"
            />

            <VideoPlayer
              v-if="modalData?.mediaType === 'video'"
              :title="modalData?.video?.videoTitle ?? undefined"
              :poster="modalData?.video?.videoCover ?? undefined"
              :path="modalData?.video?.videoUrl ?? ''"
              rounded="rounded-xs"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex gap-4 sm:gap-5 flex-col sm:flex-row-reverse w-full">
          <UButton
            v-if="modalData?.primaryAction.label"
            :label="modalData.primaryAction.label"
            :to="modalData.primaryAction.to ?? undefined"
            :block="!isTablet"
            :size="isTablet ? 'sm' : 'xs'"
            color="primary"
            variant="solid"
            trailing
            :icon="modalData.primaryAction.icon ?? undefined"
            :target="modalData.primaryAction.target ?? undefined"
            @click="handleModalClose"
          />

          <UButton
            v-if="modalData?.secondaryAction.label"
            :label="modalData.secondaryAction.label"
            :to="modalData.secondaryAction.to ?? undefined"
            :block="!isTablet"
            :size="isTablet ? 'sm' : 'xs'"
            color="neutral"
            variant="outline"
            trailing
            :icon="modalData.secondaryAction.icon ?? undefined"
            :target="modalData.secondaryAction.target ?? undefined"
            @click="handleModalClose"
          />
        </div>
      </template>
    </UModal>

    <Transition name="fade">
      <div>
        <UAlert
          v-for="alert in visibleAlerts"
          :key="alert.id"
          class="fixed bottom-0 left-0 m-4 max-w-96 w-[calc(100%-2rem)] bg-default shadow-md z-10000000000"
          :ui="{ title: 'body -mt-1', actions: 'justify-end', description: !alert.popup.title ? ' mt-0' : '', root: 'relative overflow-hidden w-full rounded-lg p-4 flex gap-2.5' }"
          :color="alert.popup.color"
          variant="outline"
          :title="alert.popup.title"
          :description="alert.popup.description"
          :icon="alert.popup.icon"
          :actions="[
            {
              label: alert.popup.action.label ?? undefined,
              color: alert.popup.color,
              variant: alert.popup.color === 'neutral' ? 'solid' : 'soft',
              to: alert.popup.action.to ?? undefined,
              target: alert.popup.action.target,
              icon: alert.popup.action.icon ?? undefined,
              trailing: true,
              onClick: () => handleAlertAction(alert.id),
            },
          ]"
        />
      </div>
    </Transition>
  </ClientOnly>
</template>
